import { apiClient } from './apiClient';
import type { ProjectDetails } from '@/types/project';

/**
 * Convert API response dates (epoch milliseconds) to Date objects
 */
function mapProjectDates(project: any): ProjectDetails {
  return {
    ...project,
    timeline: {
      ...project.timeline,
      startDate: new Date(project.timeline.startDate),
      endDate: new Date(project.timeline.endDate),
      milestones: project.timeline?.milestones?.map((m: any) => ({
        ...m,
        dueDate: new Date(m.dueDate),
      })) || [],
    },
    teamMembers: project.teamMembers?.map((member: any) => ({
      ...member,
      startDate: new Date(member.startDate),
      endDate: member.endDate ? new Date(member.endDate) : undefined,
    })) || [],
    createdAt: new Date(project.createdAt || Date.now()),
    updatedAt: new Date(project.updatedAt || Date.now()),
    lastActivityAt: project.lastActivityAt ? new Date(project.lastActivityAt) : undefined,
  };
}

export const projectsApi = {
  /**
   * Fetch all projects with rich details from the API
   * @returns Promise<ProjectDetails[]> Array of projects with rich details
   * @throws ApiError if the request fails
   */
  async getAllProjects(): Promise<ProjectDetails[]> {
    try {
      const projects = await apiClient.get<any[]>('/projects/details');
      return projects.map(mapProjectDates);
    } catch (error) {
      console.error('Failed to fetch projects from API:', error);
      throw error;
    }
  },

  /**
   * Fetch a single project by ID with rich details from the API
   * @param id Project ID
   * @returns Promise<ProjectDetails> Project with rich details
   * @throws ApiError if the request fails
   */
  async getProjectById(id: string): Promise<ProjectDetails> {
    try {
      const project = await apiClient.get<any>(`/projects/${id}/details`);
      return mapProjectDates(project);
    } catch (error) {
      console.error(`Failed to fetch project ${id} from API:`, error);
      throw error;
    }
  },
};
